import React from "react";
import { Divider, Grid, Typography } from "@mui/material";

import { COUNTRIES, STATES, TIMEZONES } from "@constants";
import { RowItem } from "../Profile/Member/RowItem";
import { formatToPhoneNumber } from "utils/formatFormFields";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";

interface ResearchParticipant {
  id: number;
  fullname: string;
  agreeToResearchParticipation: boolean;
}

interface TeamMemberDetailProps {
  member: any;
  researchParticipants: ResearchParticipant[] | null;
  onClosePanel: () => void;
  teamRoles?: any[];
}

export default function TeamMemberDetail({
  member,
  researchParticipants,
  teamRoles = [],
}: TeamMemberDetailProps) {
  const {
    firstName,
    lastName,
    dateOfBirth,
    gender,
    phoneNumber,
    email,
    address1,
    address2,
    country,
    state,
    city,
    zipCode,
    timezone,
    notificationPreferences,
    roleId,
    legalGuardian,
    documentSigner,
  } = member;

  const dateOfBirthFormatted = dateOfBirth
    ? new Date(dateWithoutTimezone(dateOfBirth)).toLocaleDateString()
    : null;
  const memberGender = gender !== "Unknown" ? gender : null;
  const electronicCommunications =
    notificationPreferences?.sendEmail || notificationPreferences?.sendSms;

  const relationshipName = teamRoles.find((role) => role.id === roleId)?.name || "-";

  return (
    <>
      <Grid container rowSpacing={5} component="section">
        <Grid item xs={12}>
          <Typography component="h3" variant="subtitle2">
            Demographics
          </Typography>
        </Grid>
        <RowItem label="Relationship" value={relationshipName} enableCopy />
        <RowItem label="Legal guardian" value={legalGuardian ? "Yes" : "No"} enableCopy />
        <RowItem label="Designated caregiver" value={documentSigner ? "Yes" : "No"} enableCopy />
        <RowItem label="First name" value={firstName} enableCopy />
        <RowItem label="Last name" value={lastName} enableCopy />
        <RowItem label="Date of birth" value={dateOfBirthFormatted} enableCopy />
        <RowItem label="Gender" value={memberGender} enableCopy />
        <RowItem label="Preferred language" value={member.preferredLanguage} enableCopy />
        <RowItem label="Phone number" value={formatToPhoneNumber(phoneNumber)} enableCopy />
        <RowItem label="Email address" value={email} enableCopy />
        <RowItem label="Address 1" value={address1} enableCopy />
        <RowItem label="Address 2" value={address2} enableCopy />
        <RowItem label="Country" value={COUNTRIES[country]} enableCopy />
        <RowItem label="State" value={STATES[state]} enableCopy />
        <RowItem label="City" value={city} enableCopy />
        <RowItem label="ZIP code" value={zipCode} enableCopy />
        <RowItem label="Time zone" value={TIMEZONES[timezone]} enableCopy />
        <RowItem
          label="Electronic communications"
          value={electronicCommunications ? "Yes" : "No"}
          enableCopy
        />
      </Grid>

      <Divider sx={{ marginTop: 5 }} />

      {researchParticipants && researchParticipants?.length > 0 && (
        <Grid container rowSpacing={5} marginTop={2}>
          <Grid item xs={12}>
            <Typography component="h3" variant="subtitle2">
              Research Participation
            </Typography>
          </Grid>

          {researchParticipants.map((participant) => (
            <RowItem
              key={participant.id}
              label={participant.fullname}
              value={participant.agreeToResearchParticipation ? "Yes" : "No"}
            />
          ))}
        </Grid>
      )}
    </>
  );
}
